import { getCountries, getCountryCallingCode } from "libphonenumber-js"

// Every dial code libphonenumber-js knows, not just the countries the
// caller-number pool currently holds numbers for — the pool can grow, and a
// rep dialling a country we cannot buy a local number for still needs to
// reach it correctly rather than have no code to pick at all. No country
// names/flags here (no such dataset in this repo or in antd) — ISO code is
// the label, so "+49 (DE)" rather than "+49 Germany".
//
// Deduped by dial code, not one row per ISO: +1 alone covers 25 countries
// (US, Canada, most of the Caribbean), so listing every ISO would repeat the
// same code 25 times. Kept as isos (plural) rather than picking one, because
// which of those 25 the rep means is genuinely ambiguous from the code
// alone — callers should only trust a code's ISO when isos.length is
// exactly 1, and leave it unset otherwise rather than guessing.
export type DialCodeEntry = { value: string; isos: string[] }

export const DIAL_CODES: DialCodeEntry[] = (() => {
  const byCode = new Map<string, string[]>()
  for (const iso of getCountries()) {
    const value = `+${getCountryCallingCode(iso)}`
    if (!byCode.has(value)) byCode.set(value, [])
    byCode.get(value)!.push(iso)
  }
  return [...byCode.entries()]
    .map(([value, isos]) => ({ value, isos }))
    .sort((a, b) => Number(a.value.slice(1)) - Number(b.value.slice(1)))
})()

export function findDialCode(number: string): DialCodeEntry | undefined {
  // Longest code first: a couple of country codes are one another's
  // prefix, so the more specific match has to win over a shorter
  // accidental one.
  return [...DIAL_CODES]
    .sort((a, b) => b.value.length - a.value.length)
    .find((c) => number.startsWith(c.value))
}

export function dialCodeOptions() {
  return DIAL_CODES.map((c) => ({
    value: c.value,
    isos: c.isos,
    // A shared code lists every ISO it covers (+1 alone has 25 — US, Canada,
    // most of the Caribbean), which as a label reads as an unreadable wall of
    // country codes. US is the overwhelmingly common case among reps, so it
    // is shown alone when present rather than the full list.
    label: `${c.value} (${c.isos.includes("US") ? "US" : c.isos.join("/")})`,
    // rc-select falls back to the option's own label as a native title="..."
    // attribute whenever nothing shorter-circuits it first — an unwanted
    // tooltip repeating what's already shown in the closed field.
    title: ""
  }))
}
